import React from 'react';
import { Button as MuiButton } from '@mui/material';
import PropTypes from 'prop-types';

/**
 * Componente de botão personalizado que encapsula o Button do MUI
 * com estilos predefinidos usados em toda a aplicação.
 */
const Button = ({ 
  children, 
  variant = 'contained', 
  color = 'primary',
  size = 'medium',
  startIcon,
  endIcon,
  fullWidth = false,
  disabled = false,
  onClick,
  buttonType = 'default',
  sx = {},
  ...props 
}) => {
  // Estilos padrão de acordo com o tipo de botão
  const getButtonStyles = () => {
    switch (buttonType) {
      case 'primary':
        return {
          py: 1.5, 
          px: 4, 
          borderRadius: 30, 
          fontWeight: 600,
          backgroundColor: '#121C42',
          color: 'white',
          '&:hover': { 
            backgroundColor: '#1a2962' 
          }
        };
      case 'secondary':
        return {
          py: 1, 
          px: 3, 
          backgroundColor: '#FDA506', 
          color: 'rgba(0, 0, 0, 0.87)', 
          fontWeight: 600, 
          borderRadius: 8,
          '&:hover': { 
            backgroundColor: '#e89900' 
          },
          '&.Mui-disabled': { 
            backgroundColor: 'rgba(253, 165, 6, 0.5)', 
            color: 'rgba(0, 0, 0, 0.38)' 
          }
        };
      case 'outlined':
        return {
          py: 1, 
          px: 4, 
          color: 'white', 
          borderColor: 'white', 
          borderRadius: 30, 
          '&:hover': { 
            borderColor: 'white', 
            backgroundColor: 'rgba(255,255,255,0.1)' 
          }
        };
      case 'add':
        return {
          py: 0.7, 
          borderRadius: 8, 
          fontWeight: 500, 
          backgroundColor: '#FDA506', 
          color: '#000', 
          fontSize: '0.9rem', 
          '&:hover': { 
            backgroundColor: '#f59c00' 
          }
        };
      case 'calculate':
        return {
          py: 0.7, 
          backgroundColor: '#121C42', 
          color: 'white', 
          borderRadius: 8, 
          fontWeight: 600, 
          fontSize: '0.9rem', 
          '&:hover': { 
            backgroundColor: '#1a2962' 
          }
        };
      case 'success':
        return {
          bgcolor: '#4CAF50', 
          color: 'white', 
          borderRadius: 30, 
          px: 3, 
          py: 1, 
          '&:hover': { 
            bgcolor: '#388E3C' 
          }
        };
      case 'default':
      default:
        return {
          borderRadius: 8
        };
    }
  };

  return (
    <MuiButton
      variant={variant}
      color={color}
      size={size}
      startIcon={startIcon}
      endIcon={endIcon}
      fullWidth={fullWidth}
      disabled={disabled}
      onClick={onClick}
      sx={{ ...getButtonStyles(), ...sx }}
      {...props}
    >
      {children}
    </MuiButton>
  );
};

Button.propTypes = {
  /** Conteúdo do botão */
  children: PropTypes.node.isRequired,
  /** Variante do botão (contained, outlined, text) */
  variant: PropTypes.string,
  /** Cor do botão (primary, secondary, error, etc.) */
  color: PropTypes.string,
  /** Tamanho do botão (small, medium, large) */
  size: PropTypes.string,
  /** Ícone inicial do botão */
  startIcon: PropTypes.node,
  /** Ícone final do botão */
  endIcon: PropTypes.node,
  /** Se o botão deve ocupar toda a largura disponível */
  fullWidth: PropTypes.bool,
  /** Se o botão está desabilitado */
  disabled: PropTypes.bool,
  /** Função chamada ao clicar no botão */
  onClick: PropTypes.func,
  /** Tipo de botão personalizado (primary, secondary, outlined, add, calculate, success) */
  buttonType: PropTypes.string,
  /** Estilos adicionais (objeto sx do MUI) */
  sx: PropTypes.object,
};

export default Button; 